import styled from "@emotion/styled/macro";
import { tokens, Typography } from "@sunrun/experience-ui-components";
import { ReactComponent as SolarGaugeIllustration } from "components/assets/images/offer-review-comparison-gauge-solar.svg";
import { ReactComponent as UtilityGaugeIllustration } from "components/assets/images/offer-review-comparison-gauge-utility.svg";
import { OFFER_REVIEW_COMPARISON_GAUGE_STROKE } from "constants/offerReviewComparisonColors";
import { formatNumber } from "utils/formatHelpers";

type GaugeVariant = "negative" | "positive";

type OfferReviewComparisonGaugeProps = {
  percent: number;
  variant: GaugeVariant;
  label: string;
  providerName?: string;
  sublabel?: string;
};

/** Matches Figma solar gauge SVG stroke geometry (avoids clipping at viewBox edges). */
const GAUGE_CX = 83.742;
const GAUGE_CY = 83.6873;
const GAUGE_RADIUS = 81.20895;
const GAUGE_STROKE = 5.06445;
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS;

const UTILITY_GAUGE_CX = 84.1072;
const UTILITY_GAUGE_CY = 83.0877;

/** Center percent in Figma gauge art. */
const GAUGE_PERCENT_FONT_SIZE = 28;
/** Tagline baked into Figma gauge art (e.g. “Energy locked in with”). */
const GAUGE_TAGLINE_FONT_SIZE = 12;
/** ~2px larger than the tagline. */
const GAUGE_PROVIDER_FONT_SIZE = 14;

const ENERGY_LOCKED_IN_TAGLINE = "Energy Locked in with";

const GAP_PERCENT_TO_TAGLINE_PX = 8;
const GAP_TAGLINE_TO_PROVIDER_PX = 16;

/** ViewBox units match CSS px at 168px rendered width. */
const getGaugeInnerLabelBaselines = (centerY: number) => {
  const percentBaseline = centerY - 14;
  const taglineBaseline =
    percentBaseline +
    GAUGE_PERCENT_FONT_SIZE * 0.3 +
    GAP_PERCENT_TO_TAGLINE_PX +
    GAUGE_TAGLINE_FONT_SIZE * 0.85;
  const providerBaseline =
    taglineBaseline +
    GAUGE_TAGLINE_FONT_SIZE * 0.15 +
    GAP_TAGLINE_TO_PROVIDER_PX +
    GAUGE_PROVIDER_FONT_SIZE * 0.85;

  return { percentBaseline, taglineBaseline, providerBaseline };
};

type GaugeInnerLabelsProps = {
  centerX: number;
  centerY: number;
  percent: number;
  providerLabel: string;
};

const GaugeInnerLabels = ({
  centerX,
  centerY,
  percent,
  providerLabel,
}: GaugeInnerLabelsProps) => {
  const { percentBaseline, taglineBaseline, providerBaseline } =
    getGaugeInnerLabelBaselines(centerY);

  return (
    <>
      <GaugeCenterPercent x={centerX} y={percentBaseline}>
        {formatNumber(percent)}%
      </GaugeCenterPercent>
      <GaugeTagline x={centerX} y={taglineBaseline}>
        {ENERGY_LOCKED_IN_TAGLINE}
      </GaugeTagline>
      <GaugeProviderName x={centerX} y={providerBaseline}>
        {providerLabel}
      </GaugeProviderName>
    </>
  );
};

const OfferReviewComparisonGauge = ({
  percent,
  variant,
  label,
  providerName,
  sublabel,
}: OfferReviewComparisonGaugeProps) => {
  const isUtility = variant === "negative";
  const clampedPercent = Math.min(100, Math.max(0, Math.round(percent)));
  const dashOffset = GAUGE_CIRCUMFERENCE * (1 - clampedPercent / 100);
  const providerLabel =
    providerName?.trim() || (isUtility ? "Utility" : "Sunrun");
  const gaugeAriaLabel = label
    ? `${label}, ${providerLabel}, ${clampedPercent} percent`
    : `${providerLabel}, ${clampedPercent} percent`;

  return (
    <GaugeBlock>
      {isUtility ? (
        <UtilityGaugeStack role="img" aria-label={gaugeAriaLabel}>
          <UtilityGaugeBackground aria-hidden />
          <GaugeTextOverlay viewBox="0 0 168 167">
            <GaugeInnerLabels
              centerX={UTILITY_GAUGE_CX}
              centerY={UTILITY_GAUGE_CY}
              percent={clampedPercent}
              providerLabel={providerLabel}
            />
          </GaugeTextOverlay>
        </UtilityGaugeStack>
      ) : (
        <>
          <SolarGaugeStack role="img" aria-label={gaugeAriaLabel}>
            <SolarGaugeBackground aria-hidden />
            <GaugeTextOverlay viewBox="0 0 168 168">
              <GaugeProgress
                cx={GAUGE_CX}
                cy={GAUGE_CY}
                r={GAUGE_RADIUS}
                fill="none"
                stroke={OFFER_REVIEW_COMPARISON_GAUGE_STROKE}
                strokeWidth={GAUGE_STROKE}
                strokeLinecap="round"
                strokeDasharray={GAUGE_CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${GAUGE_CX} ${GAUGE_CY})`}
              />
              <GaugeInnerLabels
                centerX={GAUGE_CX}
                centerY={GAUGE_CY}
                percent={clampedPercent}
                providerLabel="Sunrun"
              />
            </GaugeTextOverlay>
          </SolarGaugeStack>
          {label ? (
            <GaugeLabel size={12} color={tokens.BLACK} align="center">
              {label}
            </GaugeLabel>
          ) : null}
          {sublabel && (
            <GaugeSublabel size={10} color={tokens.BLACK} align="center">
              {sublabel}
            </GaugeSublabel>
          )}
        </>
      )}
    </GaugeBlock>
  );
};

const GaugeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 168px;
`;

const gaugeStackStyles = `
  position: relative;
  width: 168px;
  height: 168px;
  flex-shrink: 0;
`;

const UtilityGaugeStack = styled.div`
  ${gaugeStackStyles}
`;

const SolarGaugeStack = styled.div`
  ${gaugeStackStyles}
`;

const gaugeBackgroundStyles = `
  display: block;
  width: 168px;
  height: 168px;

  [data-offer-review-gauge-labels="true"] {
    display: none;
  }
`;

const UtilityGaugeBackground = styled(UtilityGaugeIllustration)`
  ${gaugeBackgroundStyles}
`;

const SolarGaugeBackground = styled(SolarGaugeIllustration)`
  ${gaugeBackgroundStyles}
`;

const GaugeTextOverlay = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
`;

const GaugeCenterPercent = styled.text`
  text-anchor: middle;
  fill: ${tokens.BLACK};
  font-size: ${GAUGE_PERCENT_FONT_SIZE}px;
  font-weight: 600;
`;

const GaugeTagline = styled.text`
  text-anchor: middle;
  fill: ${tokens.BLACK};
  font-size: ${GAUGE_TAGLINE_FONT_SIZE}px;
  font-weight: 600;
`;

const GaugeProviderName = styled.text`
  text-anchor: middle;
  fill: ${tokens.BLACK};
  font-size: ${GAUGE_PROVIDER_FONT_SIZE}px;
  font-weight: 600;
`;

const GaugeProgress = styled.circle`
  transition: stroke-dashoffset 0.4s ease;
`;

const GaugeLabel = styled(Typography)`
  display: block;
  width: 100%;
  margin-top: 4px;
  line-height: 1.2 !important;
  text-align: center;
`;

const GaugeSublabel = styled(Typography)`
  display: block;
  width: 100%;
  margin-top: 2px;
  line-height: 1.2 !important;
  text-align: center;
`;

export { OfferReviewComparisonGauge };
